import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { logWarn } from "@/lib/logger";

/**
 * Polls /notifications/unread-count every `intervalMs` (default 15s) and
 * whenever `refreshKey` changes. Returns { count, refresh, markAllRead }.
 * `enabled=false` fully disables polling (e.g. when the user is logged out).
 *
 * When new notifications arrive (delta > 0 vs previous count and previous
 * count > 0 — never on first load), fetches the latest unread ones and
 * shows a toast per subject with an optional deep link.
 */
export default function useUnreadNotifications({ enabled = true, intervalMs = 15_000, showToasts = true, onNewNotification } = {}) {
  const [count, setCount] = useState(0);
  const previousCount = useRef(null);
  const seenIds = useRef(new Set());

  const showToastForNew = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      const unread = (data || []).filter((n) => !n.read).slice(0, 5);
      const fresh = unread.filter((n) => !seenIds.current.has(n.id));
      fresh.forEach((n) => {
        seenIds.current.add(n.id);
        if (showToasts) {
          toast(n.subject || "Nuova notifica", {
            description: n.body ? String(n.body).slice(0, 140) : undefined,
            action: n.link_url
              ? { label: "Apri", onClick: () => { window.location.href = n.link_url; } }
              : undefined,
            duration: 8000,
          });
        }
        onNewNotification?.(n);
      });
      // Prime seen ids on first load so we don't spam toasts for pre-existing unread.
      if (previousCount.current === null) {
        (data || []).forEach((n) => { if (!n.read) seenIds.current.add(n.id); });
      }
    } catch (e) { logWarn("fetch new notifications failed", e); }
  }, [onNewNotification, showToasts]);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await api.get("/notifications/unread-count");
      const next = data?.count ?? 0;
      const prev = previousCount.current;
      setCount(next);
      if (prev === null) {
        // First load — prime seen set, no toast.
        previousCount.current = next;
        await showToastForNew();
      } else if (next > prev) {
        previousCount.current = next;
        await showToastForNew();
      } else {
        previousCount.current = next;
      }
    } catch (e) { logWarn("unread-count fetch failed", e); }
  }, [enabled, showToastForNew]);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/notifications/read-all");
      setCount(0);
      previousCount.current = 0;
    } catch (e) { logWarn("mark-all-read failed", e); }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const id = setInterval(refresh, intervalMs);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [enabled, intervalMs, refresh]);

  return { count, refresh, markAllRead };
}
